// ═══════════════════════════════════════════════════════════════════
//  CADUCIDAD DE LAS AUTORIZACIONES DE RUTA (entrega 300)
//
//  Lo que pidió Manuel: "ponme fecha de caducidad, que me avise de las que
//  tenga caducadas en Avisos y al entrar; nuevo permiso avisos caducidad
//  autorizaciones".
//
//  Dos piezas, las dos con el permiso `avisos_autorizaciones` (ver):
//
//  · BloqueCaducidadAutorizaciones — en la pantalla de Avisos: las
//    caducadas (en rojo) y las que caducan en los próximos 30 días (ámbar),
//    con enlace a Autorizaciones de ruta para cambiarlas.
//
//  · AvisoCaducidadAlEntrar — al entrar en el programa, si hay alguna
//    CADUCADA, una ventana con la lista. Sale UNA vez por sesión (no cada vez
//    que se cambia de pantalla); se guarda en sessionStorage, y si el
//    navegador no deja guardar, simplemente puede volver a salir.
//
//  Los datos los da el servidor: GET /autorizaciones-ruta/caducidades.
// ═══════════════════════════════════════════════════════════════════
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import Modal from './Modal';

const ddmmaaaa = (iso) => (iso ? String(iso).slice(0, 10).split('-').reverse().join('/') : '');
const ICONO = { escolar: '🎒', obrera: '🏭' };
const TIPO = { escolar: 'Escolar', obrera: 'Obrero' };

function textoDias(a) {
  if (a.dias < 0) return `caducó hace ${-a.dias} día${a.dias === -1 ? '' : 's'}`;
  if (a.dias === 0) return 'caduca HOY';
  return `caduca en ${a.dias} día${a.dias === 1 ? '' : 's'}`;
}

function Lista({ filas, color, dataLista }) {
  return (
    <ul data-lista={dataLista} style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
      {filas.map(a => (
        <li key={a.id} data-autorizacion-caducidad={a.id} style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap', fontSize: 13 }}>
          <span aria-hidden="true">{ICONO[a.tipo] || '📄'}</span>
          <b>{a.nombre}</b>
          <span style={{ color: 'var(--rg-gray-500)', fontSize: 12 }}>{TIPO[a.tipo] || a.tipo}</span>
          <span style={{ color, fontWeight: 700 }}>{ddmmaaaa(a.fecha_caducidad)} · {textoDias(a)}</span>
        </li>
      ))}
    </ul>
  );
}

// ─── En la pantalla de Avisos ──────────────────────────────────────
export function BloqueCaducidadAutorizaciones() {
  const { puede } = useAuth();
  const navigate = useNavigate();
  const ver = puede('avisos_autorizaciones', 'ver');
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!ver) return;
    api.get('/autorizaciones-ruta/caducidades')
      .then(({ data }) => setDatos(data))
      .catch(err => setError(err.response?.data?.error || 'No se pudieron mirar las autorizaciones'));
  }, [ver]);

  if (!ver) return null;
  const caducadas = datos?.caducadas || [];
  const proximas = datos?.proximas || [];
  return (
    <div className="card" data-bloque-caducidad-autorizaciones style={{ marginBottom: 16,
      borderLeft: `4px solid ${caducadas.length ? '#B91C1C' : (proximas.length ? '#D97706' : '#16a34a')}` }}>
      <div className="card-body">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
          <h3 style={{ margin: 0, fontSize: 16 }}>📑 Autorizaciones de ruta</h3>
          {caducadas.length > 0 && <span data-cuenta-caducadas style={{ background: '#B91C1C', color: '#fff', borderRadius: 999, padding: '1px 8px', fontSize: 12, fontWeight: 700 }}>{caducadas.length} caducada{caducadas.length === 1 ? '' : 's'}</span>}
          {proximas.length > 0 && <span data-cuenta-proximas style={{ background: '#D97706', color: '#fff', borderRadius: 999, padding: '1px 8px', fontSize: 12, fontWeight: 700 }}>{proximas.length} por caducar</span>}
          <button type="button" className="btn btn-secondary btn-sm" style={{ marginLeft: 'auto' }}
            onClick={() => navigate('/autorizaciones-ruta')}>Ir a Autorizaciones de ruta</button>
        </div>
        {error && <p style={{ color: 'var(--rg-danger)', margin: 0 }}>{error}</p>}
        {!error && !datos && <div className="spinner" />}
        {datos && !caducadas.length && !proximas.length && (
          <p data-sin-caducidades style={{ color: 'var(--rg-gray-500)', margin: 0, fontSize: 13 }}>
            Ninguna caducada ni a punto de caducar (próximos {datos.dias_aviso} días).
          </p>
        )}
        {caducadas.length > 0 && (
          <div style={{ marginBottom: proximas.length ? 10 : 0 }}>
            <div style={{ fontWeight: 700, color: '#B91C1C', fontSize: 13, marginBottom: 4 }}>⛔ Caducadas</div>
            <Lista filas={caducadas} color="#B91C1C" dataLista="caducadas" />
          </div>
        )}
        {proximas.length > 0 && (
          <div>
            <div style={{ fontWeight: 700, color: '#92400E', fontSize: 13, marginBottom: 4 }}>⏳ Caducan en los próximos {datos.dias_aviso} días</div>
            <Lista filas={proximas} color="#92400E" dataLista="proximas" />
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Al entrar en el programa ──────────────────────────────────────
const CLAVE = (uid) => `aviso_caducidad_autorizaciones_${uid || 'x'}`;
function yaAvisado(uid) {
  try { return window.sessionStorage.getItem(CLAVE(uid)) === '1'; } catch (_) { return false; }
}
function marcarAvisado(uid) {
  try { window.sessionStorage.setItem(CLAVE(uid), '1'); } catch (_) { /* sin almacenamiento: puede volver a salir */ }
}

export function AvisoCaducidadAlEntrar() {
  const { user, puede } = useAuth();
  const navigate = useNavigate();
  const ver = !!user && puede('avisos_autorizaciones', 'ver');
  const uid = user && user.id;
  const [caducadas, setCaducadas] = useState([]);
  const [abierto, setAbierto] = useState(false);

  useEffect(() => {
    if (!ver || yaAvisado(uid)) return;
    let vivo = true;
    api.get('/autorizaciones-ruta/caducidades')
      .then(({ data }) => {
        if (!vivo) return;
        marcarAvisado(uid);
        const lista = data?.caducadas || [];
        if (lista.length) { setCaducadas(lista); setAbierto(true); }
      })
      .catch(() => { /* si falla, no se molesta: ya saldrá en Avisos */ });
    return () => { vivo = false; };
  }, [ver, uid]);

  if (!abierto) return null;
  return (
    <Modal open={abierto} onClose={() => setAbierto(false)} size="md"
      title={`⛔ ${caducadas.length} autorización${caducadas.length === 1 ? '' : 'es'} de ruta caducada${caducadas.length === 1 ? '' : 's'}`}
      footer={<>
        <button className="btn btn-secondary" onClick={() => setAbierto(false)}>Cerrar</button>
        <button className="btn btn-primary" data-ir-autorizaciones
          onClick={() => { setAbierto(false); navigate('/autorizaciones-ruta'); }}>Ir a Autorizaciones de ruta</button>
      </>}>
      <div data-aviso-caducidad-al-entrar>
        <p style={{ marginTop: 0, fontSize: 13 }}>Hay que subir las nuevas: un conductor podría estar enseñando una que ya no vale.</p>
        <Lista filas={caducadas} color="#B91C1C" dataLista="caducadas-al-entrar" />
      </div>
    </Modal>
  );
}
